<template>
  <div
    class="fixed top-0 left-0 z-10 h-screen w-screen bg-nucleus-lightestBlue"
  >
    <LoadingScreen :loaded="loaded" :message="loadingMessage"></LoadingScreen>

    <div v-if="loaded">
      <HeaderMini
        :name="localUser.name"
        :to="{
          name: 'localUsersDetail',
          params: { userId: localUser.userId },
        }"
        :text="$t('global.defaults.user', 1)"
        :no-menu="true"
      ></HeaderMini>
      <HeaderShadow></HeaderShadow>
      <div class="p-4">
        <form @submit.prevent="editLocalUser">
          <div class="flex pb-4">
            <div class="mr-2 w-1/3">
              <ButtonSecondary
                type="button"
                :name="$t('buttons.cancel')"
                :disabled="loading"
                @click="pushToDetail()"
              ></ButtonSecondary>
            </div>
            <div class="ml-2 w-2/3">
              <Button
                type="submit"
                :name="$t('buttons.save')"
                :disabled="
                  !validateEmail(localUser.email) ||
                  !required(localUser.name) ||
                  !required(localUser.phone) ||
                  !validatePhoneNumber(
                    localUser.countryCode,
                    localUser.phone
                  ) ||
                  loading
                "
                :loading="loading"
              ></Button>
            </div>
          </div>

          <div
            class="bg-nucleus-white transition-all"
            :class="{ 'opacity-50': loading }"
          >
            <div class="flex items-center px-4 py-6 border-b">
              <div
                class="text-3xl leading-none p-2 mr-6 flex self-center transition bg-nucleus-primaryBlue text-nucleus-white rounded-full icon-user"
              ></div>
              <div class="text-2xl">
                {{ $t("global.defaults.edit") }} {{ localUser.name }}
              </div>
            </div>
            <div class="px-4 py-6 overflow-y-auto scroll-box">
              <Checkbox
                id="admin"
                v-model:value="localUser.role"
                :name="$t('inputs.adminRole.label')"
              ></Checkbox>
              <Input
                id="name"
                v-model:value="localUser.name"
                input-type="text"
                :label="$t('inputs.name.label')"
                :max-length="inputMaxLengths.name"
                :placeholder="$t('inputs.name.placeholder')"
                required
              />
              <div class="flex">
                <div class="mr-2 w-1/4">
                  <SelectCountry
                    id="code"
                    v-model:value="localUser.countryCode"
                    :update:value="localUser.countryCode"
                    :label="$t('inputs.code.label')"
                    :options="countryCodes"
                    :options-common="countryCodesCommon"
                  />
                </div>
                <div class="ml-2 w-3/4">
                  <Input
                    id="phone"
                    v-model:value="localUser.phone"
                    :update:value="
                      validatePhoneNumber(
                        localUser.countryCode,
                        localUser.phone
                      )
                    "
                    input-type="text"
                    :label="$t('inputs.phone.label')"
                    :max-length="inputMaxLengths.phone"
                    :placeholder="$t('inputs.phone.placeholder')"
                    :error="error.phone"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        <ErrorBox
          v-if="errorHeading || errorContent"
          :heading="errorHeading"
          :content="errorContent"
          :fixed="errorFixed"
          @closeError="
            errorHeading = '';
            errorContent = '';
          "
        ></ErrorBox>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderShadow from "@/components/utils/HeaderShadow.vue"; // @ is an alias to /src
import HeaderMini from "@/components/global/HeaderMini.vue"; // @ is an alias to /src
import Button from "@/components/utils/Button.vue"; // @ is an alias to /src
import ButtonSecondary from "@/components/utils/ButtonSecondary.vue"; // @ is an alias to /src
import Input from "@/components/utils/Input.vue"; // @ is an alias to /src
import Select from "@/components/utils/Select.vue"; // @ is an alias to /src
import SelectCountry from "@/components/utils/SelectCountry.vue"; // @ is an alias to /src
import CountryCodesJson from "@/store/data/countryCodes.json"; // @ is an alias to /src
import CountryCodesCommonJson from "@/store/data/countryCodesCommon.json"; // @ is an alias to /src
import Checkbox from "@/components/utils/Checkbox.vue"; // @ is an alias to /src
import LoadingScreen from "@/components/global/LoadingScreen.vue";
import ErrorBox from "@/components/utils/ErrorBox.vue";

import parsePhoneNumber from "libphonenumber-js";

import InputMaxLengths from "@/store/data/inputMaxLengths.json";
import { getLocalisedError } from "@/utils/apiUtils";

@Options({
  components: {
    Button,
    ButtonSecondary,
    HeaderShadow,
    HeaderMini,
    Input,
    Select,
    SelectCountry,
    Checkbox,
    LoadingScreen,
    ErrorBox,
  },
  data() {
    return {
      countryCodes: CountryCodesJson,
      countryCodesCommon: CountryCodesCommonJson,
      localUser: {},
      error: {
        email: "",
        phone: "",
      },
      correctNumber: "",
      loading: false,
      loaded: false,
      loadingMessage: this.$t("global.defaults.loadingLocal"),
      inputMaxLengths: InputMaxLengths[0],
      errorHeading: "",
      errorContent: "",
    };
  },
  beforeMount() {
    this.getLocalUser();
  },
  mounted() {
    document.title =
      this.$t("global.defaults.editLocal") +
      " - " +
      this.$t("global.defaults.browserTitle");
  },
  methods: {
    getCountryCallingCode() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let phoneNumber: any;
      phoneNumber = parsePhoneNumber(this.localUser.phone);
      this.localUser.phone = phoneNumber.nationalNumber;
      this.localUser.countryCode = String("+" + phoneNumber.countryCallingCode);
    },
    getLocalUser() {
      this.$store
        .dispatch("getLocalUser", this.$route.params.userId)
        .then((response: Record<string, unknown>) => {
          if (response.data) {
            this.loaded = true;
            this.localUser = response.data;
            this.getCountryCallingCode();
            this.getRole();
          }
        });
    },
    getRole() {
      if (this.localUser.role === "LOCAL_ADMIN") {
        this.localUser.role = true;
      } else {
        this.localUser.role = false;
      }
    },
    editLocalUser() {
      this.loading = true;

      const number = this.correctNumber
        ? this.correctNumber
        : this.remoteUser.phone;

      let role = "STAFF";
      if (this.localUser.role) role = "LOCAL_ADMIN";

      const data = {
        name: this.localUser.name,
        email: this.localUser.email,
        phone: number,
        role: role,
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.$store
        .dispatch("editLocalUser", {
          userId: this.localUser.userId,
          data: data,
        })
        .then((response: any) => {
          if (response) {
            // this.verifyUser(response.data.userId);
            this.$store.dispatch("changeUpdate", "localUser");
            this.pushToDetail();
            console.log("edit local", response);
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          this.loading = false;
          this.errorFixed = true;
          this.errorHeading = "";
          this.errorContent = getLocalisedError(error);
        });
    },
    pushToDetail() {
      this.$router.push({
        name: "localUsersDetail",
        params: { userId: this.localUser.userId },
      });
    },
    validateEmail(email: string) {
      const mailformat =
        // eslint-disable-next-line no-useless-escape
        /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

      if (this.required(email)) {
        if (!email.match(mailformat)) {
          this.error.email = this.$t("inputs.email.errorInvalid");
          return false;
        } else {
          this.error.email = "";
          return true;
        }
      } else {
        return false;
      }
    },
    validatePhoneNumber(code: any, phone: any) {
      const phoneNumber = parsePhoneNumber(
        code + phone.replace(/^0+/, "").replace(/\s/g, "")
      );

      if (this.required(phone)) {
        if (phoneNumber?.isValid()) {
          this.error.phone = "";
          this.correctNumber = phoneNumber.number;
          return true;
        } else {
          this.error.phone = this.$t("inputs.phone.errorInvalid");
          return false;
        }
      } else {
        return false;
      }
    },
    required(input: string, inputName: string) {
      if (input.length > 0) {
        if (inputName) this.error[inputName] = "";
        return true;
      } else {
        if (inputName)
          this.error[inputName] =
            inputName + " " + this.$t("error.validation.isRequired");
        return false;
      }
    },
  },
})
export default class LocalUsersEdit extends Vue {}
</script>

<style lang="scss" scoped>
.scroll-box {
  height: calc(100vh - 278px);
}
</style>
